import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, MapPin, Calendar, User, ChevronRight, Users, Heart } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function CategoryDetail() {
  const { categoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, locationData } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredSubcategory, setHoveredSubcategory] = useState(null);
  const [friendIds, setFriendIds] = useState(new Set());

  // Get subcategory from URL params
  const selectedSubcategory = searchParams.get('sub') || null;
  const selectedSubSubcategory = searchParams.get('subsub') || null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const [categoriesRes, postsRes] = await Promise.all([
          axios.get(`${API}/api/categories`),
          axios.get(`${API}/api/posts`, {
            params: { category: categoryId },
            headers
          })
        ]);

        const foundCategory = categoriesRes.data.find(c => c.id === categoryId);
        setCategory(foundCategory);
        setAllCategories(categoriesRes.data);
        setPosts(postsRes.data);
        
        // Fetch friends if logged in
        if (user) {
          try {
            const friendsRes = await axios.get(`${API}/api/friends`, { headers });
            const ids = new Set(friendsRes.data.map(f => f.user_id));
            setFriendIds(ids);
          } catch (e) {
            console.log('Could not fetch friends');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId, user]);

  // Fetch posts when subcategory changes
  useEffect(() => {
    const fetchFilteredPosts = async () => {
      if (!categoryId) return;
      
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const params = { category: categoryId };
        if (selectedSubcategory) {
          params.subcategory = selectedSubcategory;
        }
        if (selectedSubSubcategory) {
          params.subsubcategory = selectedSubSubcategory;
        }
        
        const postsRes = await axios.get(`${API}/api/posts`, { params, headers });
        setPosts(postsRes.data);
      } catch (error) {
        console.error('Error fetching filtered posts:', error);
      }
    };

    fetchFilteredPosts();
  }, [categoryId, selectedSubcategory, selectedSubSubcategory]);

  const filteredPosts = searchQuery.trim()
    ? posts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : posts;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleSubcategoryClick = (subId) => {
    if (selectedSubcategory === subId) {
      // Clicking same subcategory clears the filter
      setSearchParams({});
    } else {
      setSearchParams({ sub: subId });
    }
  };

  const handleSubSubcategoryClick = (subsubId) => {
    if (selectedSubSubcategory === subsubId) {
      setSearchParams({ sub: selectedSubcategory });
    } else {
      setSearchParams({ sub: selectedSubcategory, subsub: subsubId });
    }
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const isFriend = (userId) => friendIds.has(userId);

  // Get the selected subcategory object for showing sub-subcategories
  const selectedSubcategoryObj = category?.subcategories?.find(s => s.id === selectedSubcategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="border-b-4 border-black bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 md:h-20">
            <div className="flex items-center gap-3">
              <Sidebar />
              <Button
                data-testid="back-button"
                onClick={() => navigate('/home')}
                variant="ghost"
                className="border-2 border-black rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </div>
            <div className="ml-4 flex items-center gap-3">
              <span className="text-3xl">{category?.icon}</span>
              <h1 className="text-xl md:text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>
                {category?.name || 'Category'}
              </h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Subcategory Navigation */}
        {category?.subcategories?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-bold text-slate-500 uppercase mb-3">Browse Subcategories</h2>
            
            {/* Subcategory Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={clearFilters}
                className={`px-4 py-2 rounded-full border-2 border-black font-semibold text-sm transition-all ${
                  !selectedSubcategory 
                    ? 'bg-indigo-500 text-white shadow-brutal' 
                    : 'bg-white hover:bg-slate-100'
                }`}
              >
                All {category.name}
              </button>
              
              {category.subcategories.map((sub) => (
                <div 
                  key={sub.id} 
                  className="relative"
                  onMouseEnter={() => setHoveredSubcategory(sub.id)}
                  onMouseLeave={() => setHoveredSubcategory(null)}
                >
                  <button
                    onClick={() => handleSubcategoryClick(sub.id)}
                    className={`px-4 py-2 rounded-full border-2 border-black font-semibold text-sm transition-all flex items-center gap-2 ${
                      selectedSubcategory === sub.id 
                        ? 'bg-indigo-500 text-white shadow-brutal' 
                        : 'bg-white hover:bg-slate-100'
                    }`}
                  >
                    <span>{sub.icon}</span>
                    <span>{sub.name}</span>
                    {sub.subcategories?.length > 0 && (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  
                  {/* Sub-subcategory Dropdown on Hover */}
                  {hoveredSubcategory === sub.id && sub.subcategories?.length > 0 && (
                    <div className="absolute top-full left-0 mt-1 bg-white border-2 border-black rounded-xl shadow-brutal z-50 min-w-48 py-2">
                      {sub.subcategories.map((subsub) => (
                        <button
                          key={subsub.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchParams({ sub: sub.id, subsub: subsub.id });
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-slate-100 flex items-center gap-2 text-sm ${
                            selectedSubSubcategory === subsub.id ? 'bg-indigo-100 text-indigo-700' : ''
                          }`}
                        >
                          <span>{subsub.icon}</span>
                          <span>{subsub.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Sub-subcategory Pills (when a subcategory with children is selected) */}
            {selectedSubcategoryObj?.subcategories?.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-4 border-l-4 border-indigo-200">
                <span className="text-xs text-slate-500 font-medium flex items-center gap-1 mr-2">
                  <ChevronRight className="w-3 h-3" />
                  {selectedSubcategoryObj.name}:
                </span>
                {selectedSubcategoryObj.subcategories.map((subsub) => (
                  <button
                    key={subsub.id}
                    onClick={() => handleSubSubcategoryClick(subsub.id)}
                    className={`px-3 py-1 rounded-full border border-black font-medium text-xs transition-all ${
                      selectedSubSubcategory === subsub.id 
                        ? 'bg-purple-500 text-white' 
                        : 'bg-white hover:bg-purple-50'
                    }`}
                  >
                    {subsub.icon} {subsub.name}
                  </button>
                ))}
              </div>
            )}

            {/* Current Filter Breadcrumb */}
            {(selectedSubcategory || selectedSubSubcategory) && (
              <div className="mt-4 text-sm text-slate-600 bg-slate-100 px-4 py-2 rounded-xl inline-flex items-center gap-2">
                <span>Showing:</span>
                <span className="font-semibold text-slate-900">
                  {category.name}
                  {selectedSubcategory && ` → ${selectedSubcategoryObj?.name}`}
                  {selectedSubSubcategory && ` → ${selectedSubcategoryObj?.subcategories?.find(s => s.id === selectedSubSubcategory)?.name}`}
                </span>
                <button 
                  onClick={clearFilters}
                  className="text-indigo-600 hover:text-indigo-800 font-medium ml-2"
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-2xl">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              data-testid="search-posts-input"
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-12 pl-14 pr-6 border-2 border-black rounded-xl shadow-brutal font-medium"
            />
          </div>
        </div>

        {/* Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-300">
            <div className="text-5xl mb-4">🌟</div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
              Your kind is out there.
            </h3>
            <p className="text-slate-600 mb-4">Be the first to add a moment in this category!</p>
            {user && (
              <Button
                onClick={() => navigate('/create-post')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
              >
                Add a moment
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Circle moments indicator */}
            {user && friendIds.size > 0 && filteredPosts.some(p => isFriend(p.user_id)) && (
              <div className="mb-4 flex items-center gap-2 text-sm text-emerald-600">
                <Heart className="w-4 h-4 fill-emerald-500" />
                <span>Your circle&apos;s moments are shown first</span>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map(post => (
                <div
                  key={post.post_id}
                  data-testid={`post-card-${post.post_id}`}
                  onClick={() => navigate(`/post/${post.post_id}`)}
                  className={`bg-white rounded-2xl border-2 p-6 cursor-pointer transition-all hover:shadow-brutal ${
                    isFriend(post.user_id) ? 'border-emerald-400' : 'border-black'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {post.user_picture ? (
                        <img src={post.user_picture} alt={post.user_name} className="w-10 h-10 rounded-full border-2 border-black object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-indigo-100 rounded-full border-2 border-black flex items-center justify-center">
                          <User className="w-5 h-5 text-indigo-500" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-sm text-slate-900">{post.user_name}</p>
                          {isFriend(post.user_id) && (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Friend
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
                    {post.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{post.description}</p>

                  {post.location && (post.location.town || post.location.state) && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {[post.location.town, post.location.state].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}

                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <Badge key={index} variant="secondary" className="border border-black text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
